import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import supabase from '../supabaseClient';
import { Inp } from './ui/FormControls';
import { cargarReservas, reservaDeLote } from '../services/reservas';
import { exportarExcel, nombreArchivoExcel } from '../services/export-excel';

const MOTIVOS = ["Rotura", "Merma", "Error de registro", "Otro"];
const RANGOS_MOVIMIENTOS = [{ v: "mes", l: "Este mes" }, { v: "3meses", l: "3 meses" }, { v: "todo", l: "Todo" }];

const formatFecha = (iso) => iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("es-ES") : "—";
const formatFechaHora = (iso) => iso ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

// ── Carga de inventario por Excel ───────────────────────────────
const normalizarTexto = (v) => String(v ?? "").trim();
const esVacio = (v) => normalizarTexto(v) === "";

const normalizarFechaExcel = (valor) => {
  if (valor === null || valor === undefined || valor === "") return null;
  // Números de fecha de Excel: convertir con SSF (sin pasar por Date/zona horaria, evita desfases de ±1 día)
  if (typeof valor === "number") {
    const fecha = XLSX.SSF.parse_date_code(valor);
    if (!fecha || !fecha.y) return null;
    return `${String(fecha.y).padStart(4, "0")}-${String(fecha.m).padStart(2, "0")}-${String(fecha.d).padStart(2, "0")}`;
  }
  if (valor instanceof Date) {
    if (isNaN(valor.getTime())) return null;
    return valor.toISOString().slice(0, 10);
  }
  const str = normalizarTexto(valor);
  if (!str) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const m = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
  if (m) {
    let [, d, mo, y] = m;
    if (y.length === 2) y = "20" + y;
    return `${y.padStart(4, "0")}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return null;
};

const parsearNumeroExcel = (v) => {
  if (typeof v === "number") return v;
  const directo = Number(v);
  if (!isNaN(directo) && normalizarTexto(v) !== "") return directo;
  const limpio = normalizarTexto(v).replace(/\./g, "").replace(",", ".");
  const n = Number(limpio);
  return isNaN(n) ? 0 : n;
};

const encontrarIndiceColumna = (headerRow, patrones) => {
  for (let i = 0; i < headerRow.length; i++) {
    const h = normalizarTexto(headerRow[i]).toLowerCase();
    if (patrones.some(p => h.includes(p))) return i;
  }
  return -1;
};

const parsearExcelInventario = async (file) => {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const filas = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" });

  let headerIdx = -1;
  for (let i = 0; i < Math.min(filas.length, 15); i++) {
    const fila = (filas[i] || []).map(c => normalizarTexto(c).toLowerCase());
    if (fila.some(c => c.includes("referencia")) && fila.some(c => c.includes("caja"))) {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx === -1) throw new Error("No se encontró la fila de cabecera (columnas Referencia/Cajas) en el Excel.");

  const header = filas[headerIdx];
  const idxAlmacen = encontrarIndiceColumna(header, ["almacén", "almacen"]);
  const idxUbicacion = encontrarIndiceColumna(header, ["ubicación", "ubicacion"]);
  const idxReferencia = encontrarIndiceColumna(header, ["referencia"]);
  const idxDescripcion = encontrarIndiceColumna(header, ["descripción", "descripcion"]);
  const idxFcp = encontrarIndiceColumna(header, ["f.c.p", "fcp", "caduc"]);
  const idxCajas = encontrarIndiceColumna(header, ["cajas"]);

  if (idxReferencia === -1 || idxCajas === -1) {
    throw new Error("No se encontraron las columnas Referencia y/o Cajas en el Excel.");
  }

  const grupos = new Map();
  for (let i = headerIdx + 1; i < filas.length; i++) {
    const fila = filas[i];
    if (!fila || fila.every(esVacio)) continue;

    const almacen = idxAlmacen >= 0 ? fila[idxAlmacen] : null;
    const ubicacion = idxUbicacion >= 0 ? normalizarTexto(fila[idxUbicacion]).toLowerCase() : "";
    const referencia = normalizarTexto(fila[idxReferencia]);

    if (idxAlmacen >= 0 && esVacio(almacen)) continue;
    if (ubicacion.includes("total")) continue;
    if (!referencia) continue;

    const descripcion = idxDescripcion >= 0 ? normalizarTexto(fila[idxDescripcion]) : "";
    const fechaCaducidad = idxFcp >= 0 ? normalizarFechaExcel(fila[idxFcp]) : null;
    const cajas = idxCajas >= 0 ? parsearNumeroExcel(fila[idxCajas]) : 0;

    const key = `${referencia}|${fechaCaducidad || "null"}`;
    if (!grupos.has(key)) {
      grupos.set(key, { referencia, descripcion, fecha_caducidad: fechaCaducidad, cantidad_cajas: 0, cantidad_palets: 0 });
    }
    const g = grupos.get(key);
    g.cantidad_cajas += cajas;
    g.cantidad_palets += 1;
    if (!g.descripcion && descripcion) g.descripcion = descripcion;
  }

  return [...grupos.values()];
};

const compararExcelConStock = (lotesExcel, stockActual) => {
  const stockPorClave = new Map(stockActual.map(s => [`${s.referencia}|${s.fecha_caducidad || "null"}`, s]));
  const excelClaves = new Set(lotesExcel.map(l => `${l.referencia}|${l.fecha_caducidad || "null"}`));

  const diferencias = [];
  const nuevos = [];
  for (const l of lotesExcel) {
    const key = `${l.referencia}|${l.fecha_caducidad || "null"}`;
    const actual = stockPorClave.get(key);
    if (actual) {
      const diffCajas = l.cantidad_cajas - (actual.cantidad_cajas || 0);
      const diffPalets = l.cantidad_palets - (actual.cantidad_palets || 0);
      if (diffCajas !== 0 || diffPalets !== 0) diferencias.push({ excel: l, stock: actual, diffCajas, diffPalets });
    } else {
      nuevos.push(l);
    }
  }

  const aCero = stockActual.filter(s => !excelClaves.has(`${s.referencia}|${s.fecha_caducidad || "null"}`) && ((s.cantidad_cajas || 0) > 0 || (s.cantidad_palets || 0) > 0));

  return { diferencias, nuevos, aCero };
};

const estadoCaducidad = (iso) => {
  if (!iso) return null;
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const en30dias = new Date(hoy.getTime() + 30 * 24 * 60 * 60 * 1000);
  const fecha = new Date(`${iso}T00:00:00`);
  if (fecha <= hoy) return "vencido";
  if (fecha <= en30dias) return "proximo";
  return null;
};

// ── OPERARIO (INTERNACIONAL): INVENTARIO ──────────────────────────
export const InventarioInternacionalSection = ({ currentUser }) => {
  const almacen_id = currentUser?.almacen_id;
  const usuario = currentUser?.user;

  const [stock, setStock] = useState([]);
  const [reservasMap, setReservasMap] = useState(new Map());
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState("");

  const [ajustando, setAjustando] = useState(null);
  const [guardandoAjuste, setGuardandoAjuste] = useState(false);

  const [modoInventario, setModoInventario] = useState(false);
  const [conteo, setConteo] = useState({});
  const [resumenInventario, setResumenInventario] = useState(null);
  const [guardandoInventario, setGuardandoInventario] = useState(false);
  const [cuadrado, setCuadrado] = useState(false);

  const [cargandoExcel, setCargandoExcel] = useState(false);
  const [errorExcel, setErrorExcel] = useState("");
  const [sinDiferenciasExcel, setSinDiferenciasExcel] = useState(false);
  const [previsualizacionExcel, setPrevisualizacionExcel] = useState(null);
  const [aplicandoExcel, setAplicandoExcel] = useState(false);

  const [exito, setExito] = useState("");

  const [exportandoMovimientos, setExportandoMovimientos] = useState(false);
  const [rangoMovimientos, setRangoMovimientos] = useState("mes");
  const [generandoExportMov, setGenerandoExportMov] = useState(false);

  const cargarStock = async () => {
    if (!almacen_id) return;
    setCargando(true);
    const [{ data }, reservas] = await Promise.all([
      supabase.from('stock').select('*').eq('almacen_id', almacen_id).order('referencia').order('fecha_caducidad'),
      cargarReservas(almacen_id),
    ]);
    setStock(data || []);
    setReservasMap(reservas);
    setCargando(false);
  };

  useEffect(() => { cargarStock(); }, [almacen_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const stockFiltrado = stock.filter(s => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return true;
    return s.referencia.toLowerCase().includes(q) || (s.descripcion || "").toLowerCase().includes(q);
  });

  const totalReferencias = new Set(stock.map(s => s.referencia)).size;
  const totalCajas = stock.reduce((acc, s) => acc + (s.cantidad_cajas || 0), 0);
  const totalPalets = stock.reduce((acc, s) => acc + (s.cantidad_palets || 0), 0);

  const abrirAjuste = (fila) => setAjustando({ ...fila, nuevaCajas: String(fila.cantidad_cajas ?? 0), nuevaPalets: String(fila.cantidad_palets ?? 0), motivo: "", motivoOtro: "" });

  const ajusteValido = ajustando && ajustando.nuevaCajas !== "" && ajustando.nuevaPalets !== "" && ajustando.motivo && (ajustando.motivo !== "Otro" || ajustando.motivoOtro.trim());

  const confirmarAjuste = async () => {
    setGuardandoAjuste(true);
    try {
      const nuevaCajas = Number(ajustando.nuevaCajas) || 0;
      const nuevaPalets = Number(ajustando.nuevaPalets) || 0;
      const diffCajas = nuevaCajas - (ajustando.cantidad_cajas || 0);
      const diffPalets = nuevaPalets - (ajustando.cantidad_palets || 0);
      const motivo = ajustando.motivo === "Otro" ? ajustando.motivoOtro.trim() : ajustando.motivo;

      if (diffCajas !== 0 || diffPalets !== 0) {
        await supabase.from('movimientos_stock').insert({
          tipo: "ajuste",
          referencia: ajustando.referencia,
          cantidad_cajas: diffCajas,
          cantidad_palets: diffPalets,
          fecha_caducidad: ajustando.fecha_caducidad,
          usuario,
          almacen_id,
          motivo,
        });
        await supabase.from('stock').update({ cantidad_cajas: nuevaCajas, cantidad_palets: nuevaPalets }).eq('id', ajustando.id);
        await cargarStock();
        setExito("✅ Ajuste guardado correctamente");
        setTimeout(() => setExito(""), 2500);
      }
      setAjustando(null);
    } finally {
      setGuardandoAjuste(false);
    }
  };

  const iniciarInventario = () => {
    setConteo({});
    setResumenInventario(null);
    setCuadrado(false);
    setModoInventario(true);
  };

  const cancelarInventario = () => {
    setModoInventario(false);
    setResumenInventario(null);
    setConteo({});
  };

  const actualizarConteo = (id, campo, valor) => setConteo(c => ({ ...c, [id]: { ...c[id], [campo]: valor } }));

  const finalizarRecuento = () => {
    const diferencias = stock.map(s => {
      const contado = conteo[s.id] || {};
      const tieneConteo = (contado.cajas ?? "") !== "" || (contado.palets ?? "") !== "";
      if (!tieneConteo) return null;
      const cajasContadas = contado.cajas === "" || contado.cajas === undefined ? 0 : Number(contado.cajas);
      const paletsContados = contado.palets === "" || contado.palets === undefined ? 0 : Number(contado.palets);
      const diffCajas = cajasContadas - (s.cantidad_cajas || 0);
      const diffPalets = paletsContados - (s.cantidad_palets || 0);
      if (diffCajas === 0 && diffPalets === 0) return null;
      return { stock: s, cajasContadas, paletsContados, diffCajas, diffPalets };
    }).filter(Boolean);

    if (diferencias.length === 0) {
      setModoInventario(false);
      setCuadrado(true);
      setTimeout(() => setCuadrado(false), 3000);
      return;
    }
    setResumenInventario(diferencias);
  };

  const confirmarInventario = async () => {
    setGuardandoInventario(true);
    try {
      for (const d of resumenInventario) {
        await supabase.from('movimientos_stock').insert({
          tipo: "ajuste",
          referencia: d.stock.referencia,
          cantidad_cajas: d.diffCajas,
          cantidad_palets: d.diffPalets,
          fecha_caducidad: d.stock.fecha_caducidad,
          usuario,
          almacen_id,
          motivo: "Inventario",
        });
        await supabase.from('stock').update({ cantidad_cajas: d.cajasContadas, cantidad_palets: d.paletsContados }).eq('id', d.stock.id);
      }
      await cargarStock();
      setModoInventario(false);
      setResumenInventario(null);
      setConteo({});
      setExito("✅ Inventario actualizado correctamente");
      setTimeout(() => setExito(""), 2500);
    } finally {
      setGuardandoInventario(false);
    }
  };

  const manejarArchivoExcel = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setErrorExcel("");
    setCargandoExcel(true);
    try {
      const lotesExcel = await parsearExcelInventario(file);
      if (lotesExcel.length === 0) {
        setErrorExcel("No se encontraron líneas válidas en el Excel.");
        return;
      }
      const { diferencias, nuevos, aCero } = compararExcelConStock(lotesExcel, stock);
      if (diferencias.length === 0 && nuevos.length === 0 && aCero.length === 0) {
        setSinDiferenciasExcel(true);
        setTimeout(() => setSinDiferenciasExcel(false), 3000);
        return;
      }
      setPrevisualizacionExcel({
        diferencias,
        nuevos,
        aCero,
        totalesExcel: { lotes: lotesExcel.length, palets: lotesExcel.reduce((s, l) => s + l.cantidad_palets, 0), cajas: lotesExcel.reduce((s, l) => s + l.cantidad_cajas, 0) },
        totalesStock: { lotes: stock.length, palets: totalPalets, cajas: totalCajas },
      });
    } catch (err) {
      setErrorExcel(err.message || "No se pudo leer el archivo. Verifica que es un .xlsx válido.");
    } finally {
      setCargandoExcel(false);
    }
  };

  const confirmarCargaExcel = async () => {
    setAplicandoExcel(true);
    try {
      let ajustesAplicados = 0;

      for (const d of previsualizacionExcel.diferencias) {
        await supabase.from('movimientos_stock').insert({
          tipo: "ajuste",
          referencia: d.excel.referencia,
          cantidad_cajas: d.diffCajas,
          cantidad_palets: d.diffPalets,
          fecha_caducidad: d.excel.fecha_caducidad,
          usuario,
          almacen_id,
          motivo: "Carga Excel",
        });
        await supabase.from('stock').update({ cantidad_cajas: d.excel.cantidad_cajas, cantidad_palets: d.excel.cantidad_palets }).eq('id', d.stock.id);
        ajustesAplicados++;
      }

      for (const n of previsualizacionExcel.nuevos) {
        await supabase.from('movimientos_stock').insert({
          tipo: "ajuste",
          referencia: n.referencia,
          cantidad_cajas: n.cantidad_cajas,
          cantidad_palets: n.cantidad_palets,
          fecha_caducidad: n.fecha_caducidad,
          usuario,
          almacen_id,
          motivo: "Carga Excel",
        });
        await supabase.from('stock').insert({
          almacen_id,
          referencia: n.referencia,
          descripcion: n.descripcion,
          fecha_caducidad: n.fecha_caducidad,
          cantidad_cajas: n.cantidad_cajas,
          cantidad_palets: n.cantidad_palets,
        });
        ajustesAplicados++;
      }

      for (const s of previsualizacionExcel.aCero) {
        await supabase.from('movimientos_stock').insert({
          tipo: "ajuste",
          referencia: s.referencia,
          cantidad_cajas: -(s.cantidad_cajas || 0),
          cantidad_palets: -(s.cantidad_palets || 0),
          fecha_caducidad: s.fecha_caducidad,
          usuario,
          almacen_id,
          motivo: "Carga Excel",
        });
        await supabase.from('stock').update({ cantidad_cajas: 0, cantidad_palets: 0 }).eq('id', s.id);
        ajustesAplicados++;
      }

      await cargarStock();
      setPrevisualizacionExcel(null);
      setExito(`✅ ${ajustesAplicados} ajuste${ajustesAplicados === 1 ? "" : "s"} aplicado${ajustesAplicados === 1 ? "" : "s"}`);
      setTimeout(() => setExito(""), 3500);
    } finally {
      setAplicandoExcel(false);
    }
  };

  const exportarInventario = () => {
    const filas = stockFiltrado.map(s => {
      const reserva = reservaDeLote(reservasMap, s.referencia, s.fecha_caducidad);
      const fisicoCajas = s.cantidad_cajas ?? 0;
      const fisicoPalets = s.cantidad_palets ?? 0;
      return {
        "Referencia": s.referencia,
        "Descripción": s.descripcion || "",
        "F. Caducidad": formatFecha(s.fecha_caducidad),
        "Cajas físico": fisicoCajas,
        "Palets físico": fisicoPalets,
        "Reservado cajas": reserva.cajas,
        "Reservado palets": reserva.palets,
        "Disponible cajas": fisicoCajas - reserva.cajas,
        "Disponible palets": fisicoPalets - reserva.palets,
      };
    });
    const totales = filas.reduce((acc, f) => ({
      cajasFisico: acc.cajasFisico + f["Cajas físico"],
      paletsFisico: acc.paletsFisico + f["Palets físico"],
      reservadoCajas: acc.reservadoCajas + f["Reservado cajas"],
      reservadoPalets: acc.reservadoPalets + f["Reservado palets"],
      disponibleCajas: acc.disponibleCajas + f["Disponible cajas"],
      disponiblePalets: acc.disponiblePalets + f["Disponible palets"],
    }), { cajasFisico: 0, paletsFisico: 0, reservadoCajas: 0, reservadoPalets: 0, disponibleCajas: 0, disponiblePalets: 0 });
    filas.push({
      "Referencia": "TOTAL", "Descripción": "", "F. Caducidad": "",
      "Cajas físico": totales.cajasFisico, "Palets físico": totales.paletsFisico,
      "Reservado cajas": totales.reservadoCajas, "Reservado palets": totales.reservadoPalets,
      "Disponible cajas": totales.disponibleCajas, "Disponible palets": totales.disponiblePalets,
    });
    exportarExcel(nombreArchivoExcel("inventario", currentUser?.almacen_nombre), [{ nombre: "Inventario", filas }]);
  };

  const confirmarExportarMovimientos = async () => {
    setGenerandoExportMov(true);
    try {
      let q = supabase.from('movimientos_stock').select('*').eq('almacen_id', almacen_id).order('creado_en', { ascending: false });
      if (rangoMovimientos !== "todo") {
        const desde = new Date();
        desde.setMonth(desde.getMonth() - (rangoMovimientos === "3meses" ? 3 : 1));
        q = q.gte('creado_en', desde.toISOString());
      }
      const { data } = await q;
      const filas = (data || []).map(m => ({
        "Fecha": formatFechaHora(m.creado_en),
        "Tipo": m.tipo,
        "Referencia": m.referencia,
        "Caducidad": formatFecha(m.fecha_caducidad),
        "Cajas": m.cantidad_cajas ?? 0,
        "Palets": m.cantidad_palets ?? 0,
        "Nº albarán": m.numero_albaran || "",
        "Proveedor": m.proveedor || "",
        "Motivo": m.motivo || "",
        "Usuario": m.usuario || "",
      }));
      exportarExcel(nombreArchivoExcel("movimientos", currentUser?.almacen_nombre), [{ nombre: "Movimientos", filas }]);
      setExportandoMovimientos(false);
    } finally {
      setGenerandoExportMov(false);
    }
  };

  return (
    <div className="space-y-4">
      {exito && (
        <div className="bg-green-50 border-2 border-green-400 text-green-700 rounded-xl py-4 text-center">
          <span className="font-semibold">{exito}</span>
        </div>
      )}
      {cuadrado && (
        <div className="bg-green-50 border-2 border-green-400 text-green-700 rounded-xl py-6 text-center">
          <span className="text-xl font-semibold">✅ Inventario cuadrado</span>
        </div>
      )}
      {sinDiferenciasExcel && (
        <div className="bg-green-50 border-2 border-green-400 text-green-700 rounded-xl py-6 text-center">
          <span className="text-xl font-semibold">✅ Sin diferencias</span>
        </div>
      )}
      {errorExcel && (
        <div className="bg-red-50 border-2 border-red-400 text-red-700 rounded-xl py-4 text-center">
          <span className="font-semibold">⚠️ {errorExcel}</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow p-4 space-y-3">
        <div className="flex items-center justify-between border-b pb-2 flex-wrap gap-2">
          <h2 className="font-bold text-gray-800">📋 Inventario</h2>
          <div className="flex gap-2 flex-wrap">
            <label className={`text-xs font-semibold px-3 py-2 rounded-lg transition-all border border-gray-200 ${cargandoExcel ? "bg-gray-100 text-gray-400" : "bg-white text-gray-700 hover:bg-gray-50 cursor-pointer"}`}>
              {cargandoExcel ? "⏳ Leyendo..." : "📥 Cargar inventario desde Excel"}
              <input type="file" accept=".xlsx" className="hidden" onChange={manejarArchivoExcel} disabled={cargandoExcel} />
            </label>
            <button onClick={exportarInventario} disabled={stockFiltrado.length === 0} className="bg-white text-gray-700 text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition-all">⬇️ Exportar Excel</button>
            <button onClick={() => setExportandoMovimientos(true)} className="bg-white text-gray-700 text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all">⬇️ Exportar movimientos</button>
            <button onClick={iniciarInventario} className="bg-cyan-600 text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-cyan-700 transition-all">Realizar inventario</button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-gray-50 rounded-lg py-2">
            <p className="text-lg font-bold text-gray-800">{totalReferencias}</p>
            <p className="text-xs text-gray-500">referencias</p>
          </div>
          <div className="bg-gray-50 rounded-lg py-2">
            <p className="text-lg font-bold text-gray-800">{totalPalets}</p>
            <p className="text-xs text-gray-500">palets</p>
          </div>
          <div className="bg-gray-50 rounded-lg py-2">
            <p className="text-lg font-bold text-gray-800">{totalCajas}</p>
            <p className="text-xs text-gray-500">cajas</p>
          </div>
        </div>
        <input
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          placeholder="Buscar por referencia o descripción..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />
      </div>

      <div className="bg-white rounded-xl shadow overflow-x-auto">
        {cargando ? (
          <p className="text-sm text-gray-400 text-center py-6">Cargando stock...</p>
        ) : stockFiltrado.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">Sin resultados.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-2 py-2 text-left">Ref.</th>
                <th className="px-2 py-2 text-left">Descripción</th>
                <th className="px-2 py-2 text-left">Cad.</th>
                <th className="px-2 py-2 text-right">Físico c/p</th>
                <th className="px-2 py-2 text-right">Reservado</th>
                <th className="px-2 py-2 text-right">Disponible</th>
                <th className="px-2 py-2 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {stockFiltrado.map(s => {
                const estado = estadoCaducidad(s.fecha_caducidad);
                const filaColor = estado === "vencido" ? "bg-red-50" : estado === "proximo" ? "bg-amber-50" : "";
                const reserva = reservaDeLote(reservasMap, s.referencia, s.fecha_caducidad);
                const fisicoCajas = s.cantidad_cajas ?? 0;
                const fisicoPalets = s.cantidad_palets ?? 0;
                const sobreReservado = reserva.cajas > fisicoCajas || reserva.palets > fisicoPalets;
                return (
                  <tr key={s.id} className={`border-t hover:bg-gray-100 ${filaColor}`}>
                    <td className="px-2 py-1.5 font-semibold whitespace-nowrap">{s.referencia}</td>
                    <td className="px-2 py-1.5">
                      <span className="block max-w-[220px] truncate" title={s.descripcion}>{s.descripcion}</span>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{formatFecha(s.fecha_caducidad)}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">{fisicoCajas} / {fisicoPalets}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">{reserva.cajas} / {reserva.palets}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      {sobreReservado ? (
                        <span className="text-red-600 font-bold">⚠️ {fisicoCajas - reserva.cajas} / {fisicoPalets - reserva.palets}</span>
                      ) : (
                        <span>{fisicoCajas - reserva.cajas} / {fisicoPalets - reserva.palets}</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <button onClick={() => abrirAjuste(s)} className="bg-cyan-600 text-white text-xs px-2 py-1 rounded hover:bg-cyan-700">Ajustar</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {ajustando && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md space-y-4">
            <h3 className="font-bold text-gray-800 text-lg">✏️ Ajustar stock</h3>
            <p className="text-sm text-gray-600">{ajustando.referencia}{ajustando.descripcion ? ` — ${ajustando.descripcion}` : ""}</p>
            <p className="text-xs text-gray-500">Caduca: {formatFecha(ajustando.fecha_caducidad)}</p>
            <div className="grid grid-cols-2 gap-3">
              <Inp label="Cajas" type="number" value={ajustando.nuevaCajas} onChange={v => setAjustando(a => ({ ...a, nuevaCajas: v }))} />
              <Inp label="Palets" type="number" value={ajustando.nuevaPalets} onChange={v => setAjustando(a => ({ ...a, nuevaPalets: v }))} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">Motivo *</label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={ajustando.motivo}
                onChange={e => setAjustando(a => ({ ...a, motivo: e.target.value }))}
              >
                <option value="">Selecciona un motivo</option>
                {MOTIVOS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            {ajustando.motivo === "Otro" && (
              <Inp label="Especifica el motivo *" value={ajustando.motivoOtro} onChange={v => setAjustando(a => ({ ...a, motivoOtro: v }))} placeholder="Describe el motivo" />
            )}
            <div className="flex gap-3 pt-2">
              <button onClick={() => setAjustando(null)} disabled={guardandoAjuste} className="flex-1 bg-gray-100 text-gray-700 font-semibold py-2 rounded-xl hover:bg-gray-200 disabled:opacity-40 transition-all">Cancelar</button>
              <button onClick={confirmarAjuste} disabled={guardandoAjuste || !ajusteValido} className="flex-1 bg-cyan-600 text-white font-semibold py-2 rounded-xl hover:bg-cyan-700 disabled:opacity-40 transition-all">
                {guardandoAjuste ? "Guardando..." : "Guardar ajuste"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modoInventario && !resumenInventario && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">
            <div className="flex items-center justify-between border-b pb-2 sticky top-0 bg-white pt-2">
              <h2 className="font-bold text-gray-800">📋 Realizar inventario</h2>
              <button onClick={cancelarInventario} className="text-xs text-red-500 underline">Cancelar</button>
            </div>
            <p className="text-xs text-gray-500">Cuenta cada referencia y anota lo contado. Deja en blanco lo que no hayas contado.</p>
            <div className="space-y-2">
              {stock.map(s => (
                <div key={s.id} className="border rounded-lg p-3 space-y-2">
                  <p className="text-sm font-semibold text-gray-800">{s.referencia}{s.descripcion ? ` — ${s.descripcion}` : ""}</p>
                  <p className="text-xs text-gray-400">Caduca: {formatFecha(s.fecha_caducidad)}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Inp label="Cajas contadas" type="number" value={conteo[s.id]?.cajas ?? ""} onChange={v => actualizarConteo(s.id, "cajas", v)} placeholder="—" />
                    <Inp label="Palets contados" type="number" value={conteo[s.id]?.palets ?? ""} onChange={v => actualizarConteo(s.id, "palets", v)} placeholder="—" />
                  </div>
                </div>
              ))}
            </div>
            <button onClick={finalizarRecuento} className="w-full bg-cyan-600 text-white font-bold py-3 rounded-xl hover:bg-cyan-700 transition-all sticky bottom-4">
              Finalizar recuento →
            </button>
          </div>
        </div>
      )}

      {resumenInventario && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-4 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="font-bold text-gray-800 border-b pb-2">📋 Diferencias detectadas</h2>
            <div className="space-y-2">
              {resumenInventario.map((d, i) => (
                <div key={i} className="border rounded-lg p-3">
                  <p className="font-semibold text-gray-800 text-sm">{d.stock.referencia}{d.stock.descripcion ? ` — ${d.stock.descripcion}` : ""}</p>
                  <p className="text-xs text-gray-600">
                    Cajas: {d.stock.cantidad_cajas ?? 0} → {d.cajasContadas} ({d.diffCajas > 0 ? "+" : ""}{d.diffCajas}) · Palets: {d.stock.cantidad_palets ?? 0} → {d.paletsContados} ({d.diffPalets > 0 ? "+" : ""}{d.diffPalets})
                  </p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setResumenInventario(null)} disabled={guardandoInventario} className="flex-1 border border-gray-300 text-gray-600 font-bold py-3 rounded-xl hover:bg-gray-50 disabled:opacity-40 transition-all">
                ← Volver
              </button>
              <button onClick={confirmarInventario} disabled={guardandoInventario} className="flex-1 bg-cyan-600 text-white font-bold py-3 rounded-xl hover:bg-cyan-700 disabled:opacity-40 transition-all">
                {guardandoInventario ? "⏳ Guardando..." : "Confirmar →"}
              </button>
            </div>
          </div>
        </div>
      )}

      {previsualizacionExcel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-4 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="font-bold text-gray-800 border-b pb-2">📥 Previsualización carga Excel</h2>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-bold text-gray-500 uppercase mb-1">Excel</p>
                <p className="text-gray-700">{previsualizacionExcel.totalesExcel.lotes} lotes · {previsualizacionExcel.totalesExcel.palets} palets · {previsualizacionExcel.totalesExcel.cajas} cajas</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-bold text-gray-500 uppercase mb-1">Stock actual</p>
                <p className="text-gray-700">{previsualizacionExcel.totalesStock.lotes} lotes · {previsualizacionExcel.totalesStock.palets} palets · {previsualizacionExcel.totalesStock.cajas} cajas</p>
              </div>
            </div>

            {previsualizacionExcel.diferencias.length > 0 && (
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Diferencias ({previsualizacionExcel.diferencias.length})</p>
                <div className="space-y-2">
                  {previsualizacionExcel.diferencias.map((d, i) => {
                    const reserva = reservaDeLote(reservasMap, d.excel.referencia, d.excel.fecha_caducidad);
                    const insuficiente = d.excel.cantidad_cajas < reserva.cajas || d.excel.cantidad_palets < reserva.palets;
                    return (
                      <div key={i} className="border rounded-lg p-3">
                        <p className="font-semibold text-gray-800 text-sm">{d.excel.referencia}{d.excel.descripcion ? ` — ${d.excel.descripcion}` : ""}</p>
                        <p className="text-xs text-gray-400">Caduca: {formatFecha(d.excel.fecha_caducidad)}</p>
                        <p className="text-xs text-gray-600">
                          Cajas: {d.stock.cantidad_cajas ?? 0} → {d.excel.cantidad_cajas} (<span className={d.diffCajas > 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>{d.diffCajas > 0 ? "+" : ""}{d.diffCajas}</span>) · Palets: {d.stock.cantidad_palets ?? 0} → {d.excel.cantidad_palets} (<span className={d.diffPalets > 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>{d.diffPalets > 0 ? "+" : ""}{d.diffPalets}</span>)
                        </p>
                        {insuficiente && (
                          <p className="text-xs text-red-600 font-bold mt-1">⚠️ El nuevo físico queda por debajo de lo reservado ({reserva.cajas} cajas / {reserva.palets} palets reservados)</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {previsualizacionExcel.nuevos.length > 0 && (
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Nuevos ({previsualizacionExcel.nuevos.length})</p>
                <div className="space-y-2">
                  {previsualizacionExcel.nuevos.map((n, i) => (
                    <div key={i} className="border border-green-200 bg-green-50 rounded-lg p-3">
                      <p className="font-semibold text-gray-800 text-sm">{n.referencia}{n.descripcion ? ` — ${n.descripcion}` : ""}</p>
                      <p className="text-xs text-gray-500">Caduca: {formatFecha(n.fecha_caducidad)} · {n.cantidad_cajas} cajas · {n.cantidad_palets} palets</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {previsualizacionExcel.aCero.length > 0 && (
              <div>
                <p className="text-xs font-bold text-red-600 uppercase mb-2">⚠️ A cero ({previsualizacionExcel.aCero.length}) — estos lotes se pondrán a 0</p>
                <div className="space-y-2">
                  {previsualizacionExcel.aCero.map((s, i) => {
                    const reserva = reservaDeLote(reservasMap, s.referencia, s.fecha_caducidad);
                    const insuficiente = reserva.cajas > 0 || reserva.palets > 0;
                    return (
                      <div key={i} className="border border-red-200 bg-red-50 rounded-lg p-3">
                        <p className="font-semibold text-gray-800 text-sm">{s.referencia}{s.descripcion ? ` — ${s.descripcion}` : ""}</p>
                        <p className="text-xs text-gray-500">Caduca: {formatFecha(s.fecha_caducidad)} · actual: {s.cantidad_cajas ?? 0} cajas, {s.cantidad_palets ?? 0} palets</p>
                        {insuficiente && (
                          <p className="text-xs text-red-600 font-bold mt-1">⚠️ Este lote tiene reserva activa ({reserva.cajas} cajas / {reserva.palets} palets) y se pondrá a 0</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2 border-t">
              <button onClick={() => setPrevisualizacionExcel(null)} disabled={aplicandoExcel} className="flex-1 border border-gray-300 text-gray-600 font-bold py-3 rounded-xl hover:bg-gray-50 disabled:opacity-40 transition-all">Cancelar</button>
              <button onClick={confirmarCargaExcel} disabled={aplicandoExcel} className="flex-1 bg-cyan-600 text-white font-bold py-3 rounded-xl hover:bg-cyan-700 disabled:opacity-40 transition-all">
                {aplicandoExcel ? "⏳ Aplicando..." : "Confirmar →"}
              </button>
            </div>
          </div>
        </div>
      )}

      {exportandoMovimientos && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm space-y-4">
            <h3 className="font-bold text-gray-800 text-lg">⬇️ Exportar movimientos</h3>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">Rango</label>
              <div className="flex gap-2 mt-1">
                {RANGOS_MOVIMIENTOS.map(r => (
                  <button
                    key={r.v}
                    onClick={() => setRangoMovimientos(r.v)}
                    className={`flex-1 px-3 py-2 text-xs font-semibold rounded-lg transition-all ${rangoMovimientos === r.v ? "bg-cyan-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                  >
                    {r.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setExportandoMovimientos(false)} disabled={generandoExportMov} className="flex-1 bg-gray-100 text-gray-700 font-semibold py-2 rounded-xl hover:bg-gray-200 disabled:opacity-40 transition-all">Cancelar</button>
              <button onClick={confirmarExportarMovimientos} disabled={generandoExportMov} className="flex-1 bg-cyan-600 text-white font-semibold py-2 rounded-xl hover:bg-cyan-700 disabled:opacity-40 transition-all">
                {generandoExportMov ? "⏳ Generando..." : "Exportar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
